<template>
  <DetailInfo hideEmptyBtnList class="assessment-container" :btnList="btnList">
    <div class="assessment-content">
      <div class="fit-score-header">
        <div class="title">{{ $t(`${i18nPrefix}.title.assessmentScore`) }}</div>
        <div v-if="showViewReportBtn" class="btn-container">
          <div class="icon-view-report iconfont icononbording-PersonalInformation"></div>
          <el-button
            class="view-report-btn"
            type="text"
            @click="viewReport"
          >{{ $t(`${i18nPrefix}.btn.viewReport`) }}</el-button>
        </div>
      </div>
      <div class="fit-score-list">
        <template v-for="(item, index) in fitScoreList" >
          <div v-if="item.showSplitLine" class="split-line" :key="item.ref"></div>
          <div class="fit-score-item" :key="index">
            <div class="score-label">{{ item.label }}</div>
            <div class="fit-score" :ref="item.ref"></div>
          </div>
        </template>
      </div>
    </div>
  </DetailInfo>
</template>

<script>
import _ from 'lodash'
import * as echarts from 'echarts'
import { mapActions } from 'vuex'
import moment from 'moment'
import { generateFitEchartsOption } from '@/constant/application'
import DetailInfo from './components/detailInfo.vue'

export default {
  components: {
    DetailInfo
  },
  props: {
    candidateBaseInfo: {
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    const personality = this.candidateBaseInfo.invitation_history?.mail_personality_note
    const problemSolving = this.candidateBaseInfo.invitation_history.mail_problem_solving_note
    return {
      i18nPrefix: 'admin.application.candidate',
      invitation: {
        personality: {
          invited: Boolean(personality),
          inviteDate: moment(personality?.send_time).format('YYYY/MM/DD'),
          finished: this.candidateBaseInfo.personality_finished,
          typeId: 1
        },
        problemSolving: {
          invited: Boolean(problemSolving),
          inviteDate: moment(problemSolving?.send_time).format('YYYY/MM/DD'),
          finished: this.candidateBaseInfo.problem_solving_finished,
          typeId: 2
        }
      }
    }
  },
  computed: {
    candidateId () {
      return this.candidateBaseInfo.id
    },
    jobId () {
      return this.candidateBaseInfo.job.id
    },
    showViewReportBtn () {
      return !this.hasAnyPermission('COMPANY_SURVEY_TYPE_B') && this.candidateBaseInfo.can_view_report
    },
    btnList () {
      if (this.candidateBaseInfo.is_agency_uploaded_and_hid) {
        return []
      }
      const vm = this
      const i18nPrefix = this.i18nPrefix
      const invitation = this.invitation
      let list = [
        {
          hidden: invitation.personality.finished,
          get label () {
            const invitation = vm.invitation.personality
            if (invitation?.invited) {
              if (invitation.finished) {
                return vm.$t(`${i18nPrefix}.btn.invited`, { date: invitation.inviteDate })
              } else {
                return vm.$t(`${i18nPrefix}.btn.invitedAndResent`, { date: invitation.inviteDate })
              }
            } else {
              return vm.$t(`${i18nPrefix}.btn.inviteValueAndStrengthLabel`)
            }
          },
          onclick: this.onInvitePersonalityBtnClick
        },
        {
          hidden: invitation.problemSolving.finished,
          get label () {
            const invitation = vm.invitation.problemSolving
            if (invitation?.invited) {
              if (invitation.finished) {
                return vm.$t(`${i18nPrefix}.btn.invited`, { date: invitation.inviteDate })
              } else {
                return vm.$t(`${i18nPrefix}.btn.invitedAndResent`, { date: invitation.inviteDate })
              }
            } else {
              return vm.$t(`${i18nPrefix}.btn.inviteProblemSolveLabel`)
            }
          },
          onclick: this.onInviteProblemSolveBtnClick
        }
      ]
      list = _.chain(list)
        .filter(item => !item.hidden)
        .forEach(item => {
          Object.defineProperty(item, 'disabled', {
            enumerable: true,
            configurable: true,
            get () {
              return vm.isDisabled
            }
          })
          Object.defineProperty(item, 'tooltip', {
            enumerable: true,
            configurable: true,
            get () {
              return vm.isDisabled && vm.$t(`${vm.i18nPrefix}.message.disabledTip`)
            }
          })
        })
        .value()
      return list
    },
    fitScoreList () {
      const hasFinishedPersonality = this.candidateBaseInfo.personality_finished
      const hasFinishedProblemSolving = this.candidateBaseInfo.problem_solving_finished
      const resumeFitScore = hasFinishedPersonality ? parseInt(this.candidateBaseInfo.total_fit * 100) : null
      const cultureFit = hasFinishedPersonality ? parseInt(this.candidateBaseInfo.organization_fit * 100) : null
      const PersonalityFit = hasFinishedPersonality ? parseInt(this.candidateBaseInfo.job_fit * 100) : null
      const problemSolvingFit = hasFinishedProblemSolving ? parseInt(this.candidateBaseInfo.mental_ability * 100) : null

      const list = [
        {
          ref: 'resumeFit',
          label: this.$t(`${this.i18nPrefix}.label.resumeFit`),
          value: resumeFitScore,
          ringColor: this.getColor(resumeFitScore)
        },
        {
          ref: 'cultureFit',
          label: this.$t(`${this.i18nPrefix}.label.cultureFit`),
          value: cultureFit,
          ringColor: this.getColor(cultureFit)
        },
        {
          ref: 'personalityFit',
          label: this.$t(`${this.i18nPrefix}.label.personalityFit`),
          value: PersonalityFit,
          ringColor: this.getColor(PersonalityFit)
        },
        {
          showSplitLine: true,
          ref: 'problemSolvingFit',
          label: this.$t(`${this.i18nPrefix}.label.problemSolvingFit`),
          value: problemSolvingFit,
          ringColor: this.getColor(problemSolvingFit)
        }
      ]
      return _.filter(list, item => !item.hidden)
    }
  },
  created () {

  },
  mounted () {
    this.initFitChart()
  },
  methods: {
    ...mapActions('application', ['sendInviteNotification']),
    getColor (val) {
      const intVal = parseInt(val || 0)
      let color = 'rgb(226, 118, 118)' // red
      if (intVal > 40 && intVal < 70) {
        color = 'rgb(244, 169, 88)' // orange
      } else if (intVal >= 70) {
        color = 'rgb(81, 191, 130)' // green
      }
      return color
    },
    initFitChart () {
      _.forEach(this.fitScoreList, item => {
        let name = item.value
        if (item.value === null) {
          name = 'N/A'
        }
        const option = generateFitEchartsOption(name, item.value, item.ringColor)
        const scoreEl = this.$refs[item.ref][0]
        const scoreChart = echarts.init(scoreEl)
        scoreChart.setOption(option)
      })
    },
    viewReport () {
      window.open(`/django_rest/company/application/job/${this.jobId}/candidate/${this.candidateId}/profile-pdf/?html=1&show_base_info=0&show_report=1&show_download_icon_in_html=1`)
    },
    async sendInvitation (invitation) {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.sendInviteNotification({
          type_id: invitation.typeId,
          job_id: this.jobId,
          candidate_ids: [this.candidateId]
        })
        if (resp) {
          if (resp.success && resp.success.length > 0) {
            this.$message.success(this.$t(`${this.i18nPrefix}.message.inviteFinishAssessment.success`))
            invitation.invited = true
            invitation.inviteDate = moment().format('YYYY-MM-DD')
          } else if (resp.no_email_count && resp.no_email_count.length > 0) {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.inviteFinishAssessment.noEmailAccount`))
            invitation.finished = true // set finished=true will hide the invite button
          } else if (resp.ignore && resp.ignore.length > 0) {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.inviteFinishAssessment.hasFinished`))
            invitation.finished = true
          } else {
            this.$message.error(this.$t(`${this.i18nPrefix}.message.inviteFinishAssessment.fail`))
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    onInvitePersonalityBtnClick () {
      const invitation = this.invitation.personality
      this.sendInvitation(invitation)
    },
    onInviteProblemSolveBtnClick () {
      const invitation = this.invitation.problemSolving
      this.sendInvitation(invitation)
    }
  }
}
</script>
<style lang='scss' scoped>
.assessment-container {
  .assessment-content {
    max-width: 1000px;
    margin: 0 auto;

    .fit-score-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 20px;

      .title {
        font-size: 18px;
        font-weight: bold;
      }

      .btn-container {
        color: #0096cc;

        .icon-view-report {
          display: inline-block;
          vertical-align: middle;
          font-size: 22px;
        }

        .view-report-btn {
          display: inline-block;
          vertical-align: middle;
          font-size: 16px;
          font-weight: bold;
          color: #0096cc;
        }
      }
    }

    .fit-score-list {
      display: flex;
      justify-content: space-between;
      margin-top: 30px;
      padding: 65px 80px;
      border: 1px solid #E4E7ED;
      border-radius: 2px;

      .split-line {
        width: 1px;
        background-color: #DCDFE6;
      }

      .fit-score-item {
        position: relative;

        .score-label {
          position: absolute;
          top: 0;
          left: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 130px;
          height: 40px;
          font-size: 16px;
          color: #606266;
          font-weight: bold;
          text-align: center;
        }

        .fit-score {
          margin-top: 60px;
          width: 130px;
          height: 120px
        }
      }
    }
  }
}
</style>
